import { observer } from 'mobx-react-lite';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Slide,
  TextField,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useInjection } from 'inversify-react';
import {
  IAddServiceButtonFeatureViewModel,
  IAddServiceButtonFeatureViewModelToken
} from '@shared/types/mobx/features/osiAccruals';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { Field } from '@shared/components/form/Field';
import { useTranslation } from '@shared/utils/i18n';
import { ServiceNameField } from '@features/osi/accruals/service/add/ui/ServiceNameField';
import { yupResolver } from '@hookform/resolvers/yup';
import { serviceSchema } from '@shared/validation/osi/services';
import { AddServiceFormValues } from '@shared/types/osi/services';
import { isEmpty } from 'lodash';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import { OsiServiceAbonentsButton } from '@features/osi/accruals/abonents';
import { RefHandle } from '@features/osi/accruals/abonents/ui/OsiServiceAbonentsButton';
import { ForceUpdateConfirmDialog } from '@features/osi/accruals/service/add/ui/ForceUpdateConfirmDialog';

interface Props {
  serviceGroupId: number;
  serviceGroupCode?: string | null;
  // OSI-786: признак «произвольная (per-OSI) группа» с бэка — гейтинг чекбокса requiresMonthlyConfirmation
  // (заменил гейтинг по code.startsWith('CUSTOM_'), чтобы мигрированные группы с code=NULL тоже получили чекбокс).
  isCustom: boolean;
  accrualMethods: any[];
  defaultServiceNames: any[];
  reloadCb: any;
}

// @ts-expect-error slide not typed
const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export const AddServiceButton: React.FC<Props> = observer(
  ({ serviceGroupId, serviceGroupCode, isCustom, accrualMethods, defaultServiceNames, reloadCb }) => {
    // Фаза 3: для TARGET_PAY показываем AllowFines и TotalMonths
    const isTargetPay = serviceGroupCode === 'TARGET_PAY';
    // OSI-786: произвольная группа — для RequiresMonthlyConfirmation. Гейтинг переключён с code.startsWith('CUSTOM_')
    // на флаг isCustom с бэка, чтобы мигрированные группы «Коммунальные/Котельная/Лифт» (code=NULL) тоже получили чекбокс.
    const isCustomGroup = isCustom;
    const hookForm = useForm<AddServiceFormValues>({
      defaultValues: {
        service: {
          code: '',
          nameRu: '',
          nameKz: ''
        },
        serviceGroupId,
        // Период по умолчанию = 1 только для TARGET_PAY (разовый целевой сбор).
        // Для прочих/произвольных групп — null (бессрочно): иначе услуга с TotalMonths=1
        // деактивируется после первого начисления общим механизмом Фазы 3 на бэке (OSI-784).
        totalMonths: isTargetPay ? 1 : null
      },
      resolver: yupResolver(serviceSchema),
      mode: 'all'
    });
    const { t, fieldWithPrefix: fwp } = useTranslation();
    const vm = useInjection<IAddServiceButtonFeatureViewModel>(IAddServiceButtonFeatureViewModelToken);
    // Признак ОСИ «Начисление пени»: чекбокс allowFines показываем только если пеня включена на уровне ОСИ
    const osiModule = useInjection<OsiService>(OsiServiceIdentifier);
    const createFine = osiModule.osiInfo?.createFine ?? false;

    const abonentsTableRef = useRef<RefHandle | null>(null);

    useEffect(() => {
      vm.setHookForm(hookForm);
    }, [hookForm, vm]);

    useEffect(() => {
      vm.setReloadCb(reloadCb);
    }, [reloadCb, vm]);

    useEffect(() => {
      vm.abonentsTableRef = abonentsTableRef;
    }, [vm]);

    // OSI-805: отдаём ViewModel тот же список методов начисления, что рендерится в выпадающем списке формы.
    // Нужен в createService, чтобы по выбранному accrualMethodId достать code метода: POST /api/OsiServices
    // возвращает EF-сущность Db.OsiService без кодов, а окно выбора помещений открывается сразу после создания
    // и по коду решает, показывать «Проживают» (TARIF_1LIVING) или «Площадь».
    // Передаём именно сеттером (а не вторым аргументом createService): createService вызывается через
    // handleSubmit(vm.createService), и вторым аргументом туда react-hook-form уже передаёт submit-event —
    // менять сигнатуру нельзя, не сломав существующий поток создания услуги.
    useEffect(() => {
      vm.setAccrualMethods(accrualMethods);
    }, [accrualMethods, vm]);

    const {
      control,
      handleSubmit,
      formState: { errors },
      watch
    } = hookForm;

    const accrualMethodId = watch('accrualMethodId', accrualMethods[0]?.id);

    const getLabel = useCallback(() => {
      const method = accrualMethods.find((method) => method.id === accrualMethodId);

      if (!method) return '';
      return fwp(method, 'description');
    }, [accrualMethodId, accrualMethods, fwp]);

    return (
      <>
        {/* size="small" + текст без Typography — единый стиль с остальными кнопками на /osi/accruals */}
        {/* flexShrink:0 + nowrap — кнопка не сжимается и не переносит текст при длинном названии категории (место уступает заголовок) */}
        <Button
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          variant="outlined"
          size="small"
          startIcon={<LibraryAddIcon />}
          onClick={vm.openDialog}
        >
          {t('accruals:addService')}
        </Button>
        {/* @ts-expect-error transition component not typed */}
        <Dialog open={vm.isDialogOpen} onClose={vm.closeDialog} TransitionComponent={Transition} fullWidth>
          <DialogTitle sx={{ mb: 2 }}>{t('accruals:addServiceTitle')}</DialogTitle>
          <DialogContent>
            <Box>
              <ServiceNameField control={control} name={'service'} options={defaultServiceNames} />
              <Controller
                control={control}
                name={'accrualMethodId'}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size={'small'}
                    select
                    fullWidth
                    sx={{ mb: 2 }}
                    label={t('accruals:accrualMethod')}
                    InputLabelProps={{ shrink: true }}
                    required
                    error={Boolean(errors?.accrualMethodId)}
                    helperText={errors?.accrualMethodId?.message ? t(errors.accrualMethodId.message) : ''}
                  >
                    {accrualMethods.map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {fwp(method, 'description')}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Field
                control={control}
                name={'amount'}
                mask
                required
                fullWidth
                label={`${getLabel()}, ${t('common:tenge')}`}
                InputLabelProps={{ shrink: true }}
                type={'amount'}
                size={'small'}
                style={{ width: '100%' }}
                align="right"
                helperText={errors?.amount?.message ? t(errors.amount.message) : ''}
              />

              {/* Фаза 3: AllowFines — для TARGET_PAY по умолчанию выключен, можно включить.
                  Показываем только если на ОСИ включено «Начисление пени» (createFine) */}
              {isTargetPay && createFine && (
                <Controller
                  control={control}
                  name={'allowFines'}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={t('accruals:allowFines')}
                      sx={{ mt: 1, mb: 1, display: 'block' }}
                    />
                  )}
                />
              )}

              {/* Фаза 3: TotalMonths — количество месяцев начисления (для TARGET_PAY) */}
              {isTargetPay && (
                <Controller
                  control={control}
                  name={'totalMonths'}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === '' ? null : Number(val));
                      }}
                      size={'small'}
                      fullWidth
                      type="number"
                      label={t('accruals:totalMonths')}
                      InputLabelProps={{ shrink: true }}
                      helperText={t('accruals:totalMonthsHint')}
                      sx={{ mt: 1, mb: 1 }}
                      inputProps={{ min: 1 }}
                    />
                  )}
                />
              )}

              {/* Ежемесячное подтверждение — только для произвольных групп */}
              {isCustomGroup && (
                <Controller
                  control={control}
                  name={'requiresMonthlyConfirmation'}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={t('accruals:requiresMonthlyConfirmation')}
                      sx={{ mt: 1, mb: 1, display: 'block' }}
                    />
                  )}
                />
              )}

            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={vm.closeDialog}>
              <Typography>{t('common:close')}</Typography>
            </Button>
            <Button
              variant={'contained'}
              disabled={!isEmpty(errors) || vm.isLoading}
              onClick={handleSubmit(vm.createService)}
            >
              <Typography>{t('common:save')}</Typography>
            </Button>
          </DialogActions>
        </Dialog>
        <ForceUpdateConfirmDialog
          open={vm.isForceUpdateConfirmDialogOpen}
          onClose={vm.closeForceUpdateConfirmDialog}
          onConfirm={vm.confirmForceUpdate}
          message={vm.forceUpdateMessage}
        />
        <OsiServiceAbonentsButton isButtonHidden reloadCb={reloadCb} ref={abonentsTableRef} />
      </>
    );
  }
);
